import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, VStack } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { LiveCoachMarkdown } from './LiveCoachMarkdown';
import { buildTurns, type CoachTurn } from './api';
import { useAskCoach, useCoachEvents } from './hooks';
import './livecoach.scss';

/* inline icons (these map to @kula-ai/icons in production) */
const Sparkle = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    {...p}
  >
    <path d="M12 3l1.9 4.6L18.5 9.5 14 11.4 12 16l-2-4.6L5.5 9.5 10 7.6z" />
    <circle cx="18.5" cy="5" r="1.2" fill="currentColor" />
    <circle cx="5.5" cy="17" r="1" fill="currentColor" />
  </svg>
);
const SendIcon = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    {...p}
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);
const Chevron = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    {...p}
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);
const CandidateIcon = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="13"
    height="13"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    {...p}
  >
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20a8 8 0 0116 0" />
  </svg>
);
const ScorecardIcon = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="13"
    height="13"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    {...p}
  >
    <path
      d="M9 11l3 3L22 4M21 12v7H3V5h12"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Tool chips — the read-only sources the coach can pull, lifted from the prototype.
const TOOLS: {
  label: string;
  prompt: string;
  icon: (p: any) => JSX.Element;
}[] = [
  {
    label: 'Candidate',
    prompt: 'Tell me about the candidate',
    icon: CandidateIcon,
  },
  {
    label: 'Scorecard',
    prompt: 'Which scorecard areas are still uncovered?',
    icon: ScorecardIcon,
  },
];

// Suggested prompts — a static client-side list (the backend trail carries no per-turn follow-ups).
const SUGGESTED_PROMPTS = [
  'What should I ask next?',
  'Show the optimal solution',
  'Summarize their approach so far',
  'Are they covering the must-haves?',
];

const WELCOME =
  "Hi — I'm watching this round with you. Ask me anything about the candidate, the plan, or what's on their screen, and I'll keep nudging you on the left as things develop. 👋";

interface Props {
  // Present whenever the pane is shown (the live phase gates on a registered session); the React Query
  // hooks no-op while it's undefined.
  sessionId: number | string | undefined;
  pendingContext?: string | null;
  onContextConsumed?: () => void;
  onCollapse?: () => void;
  // Anchors a question to the current moment in the round (the running transport clock).
  clockMs?: number;
  // Live progress line while the coach runs a tool ("Pulling the scorecard…"), from the WS stream. Shown in
  // place of "Thinking…" on the pending turn.
  toolLabel?: string | null;
}

const UserBubble = ({ text }: { text: string }) => (
  <Flex direction="column" align="flex-end" gap="5px">
    <Box className="livecoach-bubble-user">{text}</Box>
  </Flex>
);

const AgentBubble = ({ children }: { children: React.ReactNode }) => (
  <Flex gap="10px" align="flex-start">
    <Flex
      flex="none"
      align="center"
      justify="center"
      w="24px"
      h="24px"
      mt="1px"
      borderRadius="7px"
      color="primary-500"
      bg="primary-50"
    >
      <Sparkle width="14" height="14" />
    </Flex>
    <Box flex={1} minW={0}>
      {children}
    </Box>
  </Flex>
);

// The coach's answer, rendered as rich Markdown under the question it threads to.
const AnswerBubble = ({ turn }: { turn: CoachTurn }) => (
  <AgentBubble>
    <Box className="livecoach-bub">
      <LiveCoachMarkdown>{turn.answer || ''}</LiveCoachMarkdown>
    </Box>
  </AgentBubble>
);

export function LiveCoachCopilot({
  sessionId,
  pendingContext,
  onContextConsumed,
  onCollapse,
  clockMs = 0,
  toolLabel = null,
}: Props) {
  const { data: events = [] } = useCoachEvents(sessionId);
  const askMut = useAskCoach(sessionId);

  const [input, setInput] = useState('');
  const [optimistic, setOptimistic] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Pair each question (category 6) with its answer (category 7, threaded by parent_event_id).
  const turns = useMemo(() => buildTurns(events), [events]);

  // An asked-but-unanswered turn drives the "thinking" indicator.
  const pending = useMemo(() => turns.some((t) => t.answer === null), [turns]);

  // Drop optimistic bubbles once the persisted trail contains them — derived at render time.
  const existingQuestions = useMemo(
    () => new Set(turns.map((t) => t.question)),
    [turns]
  );
  const visibleOptimistic = optimistic.filter((q) => !existingQuestions.has(q));

  useEffect(() => {
    scrollRef.current?.scrollTo?.({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [turns, visibleOptimistic.length, pending]);

  useEffect(() => {
    if (pendingContext) inputRef.current?.focus();
  }, [pendingContext]);

  const send = (raw: string) => {
    const typed = raw.trim();
    if (!typed) return;
    // When discussing a specific nudge, frame the question with it so the coach answers about that nudge
    // (the "Asking about" reveal shows it). Store the framed text optimistically so it matches the trail
    // row once persisted and the optimistic bubble drops cleanly.
    const question = pendingContext
      ? `About this coaching nudge — "${pendingContext}":\n\n${typed}`
      : typed;
    setOptimistic((o) => [...o, question]);
    setInput('');
    // The textarea auto-grows on input; reset it to one row so the next message starts compact.
    if (inputRef.current) inputRef.current.style.height = 'auto';
    onContextConsumed?.();
    askMut.mutate({ question, atMs: clockMs });
  };

  const isEmpty = turns.length === 0 && visibleOptimistic.length === 0;

  return (
    <Flex
      className="livecoach-copilot"
      direction="column"
      h="100%"
      bg="secondary-25"
      minW={0}
    >
      {/* header + tool chips */}
      <Box
        flex="none"
        borderBottom="1px solid"
        borderColor="secondary-100"
        bg="base-white"
      >
        <Flex align="center" gap="11px" px="18px" pt="16px" pb="12px">
          <Flex
            align="center"
            justify="center"
            w="32px"
            h="32px"
            borderRadius="9px"
            color="base-white"
            bgGradient="linear(135deg, primary-400, primary-600)"
            boxShadow="0 5px 14px -5px rgba(96,90,251,0.7)"
          >
            <Sparkle />
          </Flex>
          <Box flex={1} minW={0}>
            <KText size="sm-semi-bold" color="secondary-900" fontWeight={600}>
              Ask the coach
            </KText>
            <KText
              size="xs-regular"
              color="secondary-600"
              fontSize="11.5px"
              mt="1px"
              noOfLines={2}
            >
              Grounded in the prep, the JD, the scorecard &amp; what’s happening
              live
            </KText>
          </Box>
          {onCollapse && (
            <Flex
              as="button"
              onClick={onCollapse}
              align="center"
              justify="center"
              w="30px"
              h="30px"
              borderRadius="8px"
              color="secondary-600"
              _hover={{ bg: 'secondary-50', color: 'secondary-900' }}
            >
              <Chevron />
            </Flex>
          )}
        </Flex>
        <Flex gap="7px" px="18px" pb="14px" flexWrap="wrap">
          {TOOLS.map(({ label, prompt, icon: ToolIcon }) => (
            <Flex
              key={label}
              as="button"
              align="center"
              gap="6px"
              px="10px"
              py="6px"
              borderRadius="8px"
              bg="base-white"
              border="1px solid"
              borderColor="secondary-100"
              transition="0.15s"
              _hover={{ borderColor: 'primary-200' }}
              onClick={() => send(prompt)}
            >
              <Box color="secondary-500">
                <ToolIcon />
              </Box>
              <KText
                size="xs-semi-bold"
                color="secondary-700"
                fontSize="11.5px"
                fontWeight={600}
              >
                {label}
              </KText>
            </Flex>
          ))}
        </Flex>
      </Box>

      {/* messages */}
      <Box
        ref={scrollRef}
        className="livecoach-copilot-body livecoach-scroll"
        flex={1}
        overflowY="auto"
        px="18px"
        py="18px"
      >
        <VStack align="stretch" spacing="18px">
          {isEmpty && (
            <AgentBubble>
              <Box className="livecoach-bub">
                <LiveCoachMarkdown>{WELCOME}</LiveCoachMarkdown>
              </Box>
            </AgentBubble>
          )}

          {turns.map((turn) => (
            <Box key={turn.key}>
              <UserBubble text={turn.question} />
              {turn.answer !== null && (
                <Box mt="18px">
                  <AnswerBubble turn={turn} />
                </Box>
              )}
            </Box>
          ))}

          {visibleOptimistic.map((q, i) => (
            <UserBubble key={`opt-${i}`} text={q} />
          ))}

          {pending && !askMut.isError && (
            <Flex gap="10px" align="center" pl="2px">
              <Box className="livecoach-spin" />
              <KText
                size="sm-regular"
                className="livecoach-progress-text"
                fontSize="13px"
              >
                {toolLabel || 'Thinking…'}
              </KText>
            </Flex>
          )}

          {askMut.isError && (
            <Flex gap="8px" align="center" pl="2px">
              <KText size="sm-regular" color="status-2-dark" fontSize="13px">
                Couldn’t reach the coach — please try again.
              </KText>
            </Flex>
          )}
        </VStack>
      </Box>

      {/* composer */}
      <Box
        flex="none"
        px="16px"
        pt="12px"
        pb="16px"
        borderTop="1px solid"
        borderColor="secondary-100"
        bg="base-white"
      >
        {pendingContext && (
          <Flex
            className="livecoach-reveal"
            align="flex-start"
            gap="10px"
            mb="9px"
            p="9px 12px"
            bg="secondary-25"
            border="1px solid"
            borderColor="secondary-100"
            borderLeft="2.5px solid"
            borderLeftColor="primary-400"
            borderRadius="9px"
          >
            <Box flex={1} minW={0}>
              <KText
                size="xs-bold"
                color="secondary-900"
                fontSize="10px"
                letterSpacing="0.06em"
                textTransform="uppercase"
                mb="3px"
              >
                Asking about
              </KText>
              <KText
                size="xs-regular"
                color="secondary-700"
                fontSize="12px"
                fontStyle="italic"
                noOfLines={2}
              >
                {pendingContext}
              </KText>
            </Box>
          </Flex>
        )}

        <Flex className="livecoach-chips" gap="7px" overflowX="auto" pb="11px">
          {SUGGESTED_PROMPTS.map((label, i) => (
            <Flex
              key={`${i}-${label}`}
              as="button"
              flex="none"
              align="center"
              px="11px"
              py="7px"
              borderRadius="20px"
              bg="primary-25"
              border="1px solid"
              borderColor="primary-100"
              transition="0.15s"
              _hover={{ borderColor: 'primary-200', bg: 'primary-50' }}
              onClick={() => send(label)}
            >
              <KText
                size="xs-semi-bold"
                color="primary-700"
                fontSize="12px"
                whiteSpace="nowrap"
                fontWeight={600}
              >
                {label}
              </KText>
            </Flex>
          ))}
        </Flex>

        <Flex
          align="flex-end"
          gap="9px"
          bg="base-white"
          border="1px solid"
          borderColor="secondary-200"
          borderRadius="13px"
          pl="14px"
          pr="7px"
          py="7px"
          transition="0.15s"
          _focusWithin={{
            borderColor: 'primary-400',
            boxShadow: '0 0 0 3px rgba(96,90,251,0.12)',
          }}
        >
          <Box
            as="textarea"
            ref={inputRef as any}
            flex={1}
            rows={1}
            value={input}
            placeholder="Ask the coach…"
            onChange={(e: any) => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height =
                Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={(e: any) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send(input);
              }
            }}
            sx={{
              border: 'none',
              outline: 'none',
              resize: 'none',
              bg: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5',
              maxHeight: '120px',
              py: '5px',
              color: 'var(--kula-colors-secondary-900)',
              '::placeholder': { color: 'var(--kula-colors-secondary-500)' },
            }}
          />
          <Flex
            as="button"
            align="center"
            justify="center"
            flex="none"
            w="36px"
            h="36px"
            borderRadius="10px"
            color="base-white"
            bg="primary-500"
            opacity={!input.trim() ? 0.45 : 1}
            cursor={!input.trim() ? 'default' : 'pointer'}
            _hover={!input.trim() ? {} : { bg: 'primary-600' }}
            boxShadow="0 6px 16px -7px rgba(96,90,251,0.7)"
            onClick={() => send(input)}
          >
            <SendIcon />
          </Flex>
        </Flex>
      </Box>
    </Flex>
  );
}

export default LiveCoachCopilot;
